/**
 * Issue #1022 (round 7, D45/D144, follow-up to #891/#889) — the strings for
 * `SignInLinkCallbackNew.jsx`'s `different-account` state and its neighbours
 * (`checking`, `link-expired-or-used`, `check-failed`, `sign-out-failed`),
 * drawn in `SignInLinkOtherAccount.dc.html` (design-round-7/mvp1-gaps).
 *
 * Pulled into their own module, the `SignInLinkRequestNew.jsx`/
 * `SignInLinkNew.test.js` convention isn't followed here because these
 * strings live in a page that already has plenty of its own; a dedicated
 * module is what `signInLinkOtherAccount.test.js` pins against, source-level,
 * the same way the rest of this slice's tests pin JSX source rather than
 * mounting anything.
 *
 * Every string here is D45's own wording (agreed by Martin, 2026-09-24) or
 * the artboard's own new copy around it — `strings_not_in_app` entries
 * 11-23 in `canvas-fragment-7-gaps.json`. `TRY_AGAIN` is the one exception:
 * it is reused wording (already built elsewhere, e.g. the milestones save
 * retry), not a new string, and it is here because this is the one place
 * this screen needs it.
 */

export const TITLE = "This link is for another account";

export const LEDE =
  "You opened a sign-in link for a different account from the one signed in on this phone.";

export const LINK_SIGNS_IN = "The link signs in";

export const PHONE_SIGNED_IN_AS = "This phone is signed in as";

export const WHAT_EACH = "What each choice does";

/**
 * The full explanation sentence for "Sign out and continue" — `me` is the
 * phone's own (full) address, `other` the link's masked one. The artboard
 * bolds only the leading verb phrase ("Sign out and continue") and renders
 * the rest plain; the render site gets that split by slicing this string
 * at `SWITCH.length`, not by reconstructing the sentence a second time —
 * see `SignInLinkCallbackNew.jsx`'s own comment at that call site.
 */
export const SWITCH_EXPLAINS = (me, other) =>
  `Sign out and continue signs ${me} out on this phone, then signs in ${other}. The link is used up.`;

/** Same shape/reason as `SWITCH_EXPLAINS` above, for "Stay signed in". */
export const STAY_EXPLAINS =
  "Stay signed in keeps you as you are. The link isn't used, and still works until it expires.";

export const SWITCH = "Sign out and continue";

export const STAY = "Stay signed in";

export const CHECKING = "Checking this link…";

export const CHECKING_NOTE = "You're still signed in. Nothing changes until you choose.";

/** `me` is the phone's own (full) address. */
export const CHECK_FAILED = (me) =>
  `We couldn't check this link. You're still signed in as ${me} — nothing has changed.`;

/** `me` is the phone's own (full) address. */
export const SIGNOUT_FAILED = (me) =>
  `We couldn't sign you out, so the link wasn't used. You're still signed in as ${me}.`;

export const TRY_AGAIN = "Try again";
